import type { GroupingGroup } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'
import { useCallback } from 'react'
import React from 'react'

import FloatingPanel, { PanelIconImage } from 'components/Editor/FloatingPanel'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { NOOP } from 'constants/commonConstans'
import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'

import {
  useGroupingFormValueUpdateForm,
  useGroupingFormValueContext,
} from './GroupingFormValueContext'
import * as s from './GroupingFormValueItemGroup.module.scss'
import FloatingPanelMap from './GroupingFormValueItemGroupFloating'

const GroupingFormValueGroupItem: React.FC<IRenderItemProps<GroupingGroup>> = ({
  name,
  item,
  index,
  drag,
  min,
  max,
  value: array,
  onChange,
}) => {
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)
  const { form } = useGroupingFormValueContext()
  const setForm = useGroupingFormValueUpdateForm()
  const file = getFileByUsage(item.target.image, FileUsageTypeEnum.image)

  const onClone = useCallback(
    () =>
      onChange?.([...array, { ...R.clone(array[index]), value: generateId() }], {
        command: 'clone',
        item: array[index],
      }),
    [onChange, array, index],
  )

  const onDelete = useCallback(
    () =>
      onChange?.(
        array.filter((_item, i) => i !== index),
        { command: 'delete', item: array[index] },
      ),
    [onChange, array, index],
  )

  const handleClose = useCallback(
    () => setForm({ form: 'root', groupIndex: index, groupName: name }),
    [index, name, setForm],
  )

  const Header = (form && FloatingPanelMap[form].header) || NOOP
  const Panel = (form && FloatingPanelMap[form].panel) || NOOP

  return (
    <div className={s.root}>
      <div className={s.header} ref={drag}>
        <span className={s.title}>
          <Icon name='builderDrugAndDrop' size={KitSize.S} />
          <Typography rows={1} tooltip={item.target.label}>
            {t('elements.grouping.tags.group')} {index + 1}
          </Typography>
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name={`${name}.${index}.copy`}
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name={`${name}.${index}.delete`}
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>
      <Padding gap>
        <FloatingPanel
          header={<Header group={item} index={index} name={name} />}
          onClose={handleClose}
          panel={<Panel group={item} index={index} name={name} />}
        >
          {({ open }) => (
            <TileButton
              active={open}
              icon='otherAdd'
              name={`${name}.${index}.open`}
              styleType='ghost'
            >
              <PanelIconImage file={item.target.image} icon='otherBlock' />
              <Typography rows={1} styleType='hint'>
                {item.target.label || file?.name || t('elements.grouping.tags.group')}
              </Typography>
            </TileButton>
          )}
        </FloatingPanel>
      </Padding>
    </div>
  )
}

export default React.memo(GroupingFormValueGroupItem) as typeof GroupingFormValueGroupItem
